import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Lock, Eye, EyeOff, MailCheck } from 'lucide-react';
import FieldError from '../ui/FieldError';
import { validateRequired, validatePassword, validatePasswordMatch } from '../../utils/validation';

/**
 * PasswordField — hoisted OUT of PasswordSection so it is a stable React
 * component reference. When it lived inside PasswordSection's function body
 * (pre-Session 262), every parent re-render created a new component
 * function each keystroke, remounting the <Input> and killing focus. That's
 * why users could only type one character at a time before having to click
 * the field again.
 */
const PasswordField = ({ id, label, value, onChange, onBlur, showPw, togglePw, placeholder, error }) => (
  <div className="space-y-2">
    <Label htmlFor={id}>{label} <span className="text-red-500">*</span></Label>
    <div className="relative">
      <Input
        id={id}
        type={showPw ? 'text' : 'password'}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        placeholder={placeholder}
        className={error ? 'border-red-400' : ''}
        data-testid={`pw-${id.replace(/_/g, '-')}-input`}
      />
      <button
        type="button"
        onClick={togglePw}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
        data-testid={`pw-${id.replace(/_/g, '-')}-toggle`}
      >
        {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
    <FieldError error={error} />
  </div>
);

const PasswordSection = ({ passwordData, setPasswordData, showPasswords, setShowPasswords, onSubmit, isLoading, awaitingConfirmation }) => {
  const [touched, setTouched] = useState({});
  const onBlur = (field) => setTouched(t => ({ ...t, [field]: true }));

  const errors = {
    current_password: touched.current_password ? validateRequired(passwordData.current_password, 'Current password') : '',
    new_password: touched.new_password ? validatePassword(passwordData.new_password) : '',
    confirm_password: touched.confirm_password ? validatePasswordMatch(passwordData.new_password, passwordData.confirm_password) : '',
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setTouched({ current_password: true, new_password: true, confirm_password: true });
    const allErrors = {
      current_password: validateRequired(passwordData.current_password, 'Current password'),
      new_password: validatePassword(passwordData.new_password),
      confirm_password: validatePasswordMatch(passwordData.new_password, passwordData.confirm_password),
    };
    if (Object.values(allErrors).some(e => e)) return;
    onSubmit(e);
  };

  return (
    <Card data-testid="change-password-card" id="change-password-section">
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <Lock className="w-5 h-5" />
          <span>Change Password</span>
        </CardTitle>
        <CardDescription>Update your account password</CardDescription>
      </CardHeader>
      <CardContent>
        {/* Two-step flow reminder: the request is only staged until the
            confirmation email link is clicked. Sits above the form so it is
            visible before the founder starts typing. */}
        {awaitingConfirmation && (
          <div
            className="mb-4 flex items-start gap-3 rounded-md border border-emerald-200 bg-emerald-50/80 px-3 py-2.5"
            data-testid="pw-confirmation-notice"
          >
            <MailCheck className="w-5 h-5 text-emerald-700 shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-semibold text-emerald-900">Check your email to complete the change</p>
              <p className="text-xs text-emerald-800 mt-0.5">
                For your security, password changes must be confirmed by clicking the link we just emailed you. Your password stays the same until you confirm.
              </p>
            </div>
          </div>
        )}
        <form onSubmit={handleSubmit} className="space-y-4">
          <PasswordField
            id="current_password" label="Current Password"
            value={passwordData.current_password}
            onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
            onBlur={() => onBlur('current_password')}
            showPw={showPasswords.current}
            togglePw={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
            placeholder="Enter current password"
            error={errors.current_password}
          />
          <PasswordField
            id="new_password" label="New Password"
            value={passwordData.new_password}
            onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
            onBlur={() => onBlur('new_password')}
            showPw={showPasswords.new}
            togglePw={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
            placeholder="Enter new password"
            error={errors.new_password}
          />
          <PasswordField
            id="confirm_password" label="Confirm New Password"
            value={passwordData.confirm_password}
            onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
            onBlur={() => onBlur('confirm_password')}
            showPw={showPasswords.confirm}
            togglePw={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
            placeholder="Confirm new password"
            error={errors.confirm_password}
          />
          <Button type="submit" disabled={isLoading} className="w-full sm:w-auto" data-testid="pw-submit-btn">
            <Lock className="w-4 h-4 mr-2" />
            {isLoading ? 'Sending confirmation…' : 'Change Password'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default PasswordSection;
